import React, { useState, useEffect, useContext, useRef } from 'react'
import { Alert } from 'antd'
import CircularLoader from '../../common/CircularLoader';
import DynamicContent from "../../nav/DynamicContent";
import { ContentContext } from "../../common/ContentContext";

const DynamicActivity = ({ step, className, continueCurrentStep }) => {
    const { updateCssStylesAndClasses } = useContext(ContentContext);
    const [stepUrl, setStepUrl] = useState();
    const [params, setParams] = useState({});
    const [error, setError] = useState();
    const continueRef = useRef(continueCurrentStep);

    useEffect(() => {
        continueRef.current = continueCurrentStep;
    }, [continueCurrentStep]);

    useEffect(() => {
        if (!step) {
            setStepUrl(undefined);
            setParams({});
            setError(undefined);
            return;
        }

        if (step.endStep) {
            setStepUrl(undefined);
            setParams({});
            setError('No content available for this step.');
            return;
        }

        let locStepUrl = step.url;
        if (step.complete && step.resultUrl) {
            locStepUrl = step.resultUrl;
        }
        if (!locStepUrl) {
            setStepUrl(undefined);
            setParams({});
            setError('No content available for this step.');
            return;
        }

        const locParams = {
            stepId: step.stepId,
            stepUserId: step.stepUserId,
            conceptId: step.conceptId,
            pathwayUserId: step.pathwayUserId,
        };

        switch (step.resourceType) {
            case 'TUTORIAL':
                if (step.tutorialId) locParams.tutorialId = step.tutorialId;
                if (step.tutorialType) locParams.tutorialType = step.tutorialType;
                break;
            case 'CONCEPT':
                if (step.conceptId) locParams.conceptId = step.conceptId;
                break;
            case 'MEDIA':
                if (step.mediaId) locParams.mediaId = step.mediaId;
                if (step.mediaType) locParams.mediaType = step.mediaType;
                break;
            case 'QUESTION':
                if (step.questionId) locParams.questionId = step.questionId;
                if (step.questionType) locParams.questionType = step.questionType;
                break;
            case 'QUESTION_CLUSTER':
                if (step.questionClusterId)
                    locParams.questionClusterId = step.questionClusterId;
                if (step.questionClusterType)
                    locParams.questionClusterType = step.questionClusterType;
                break;
            case 'QUIZ':
                if (step.quizId) locParams.quizId = step.quizId;
                break;
            case 'TEST':
                if (step.testId) locParams.testId = step.testId;
                break;
            default:
                break;
        }

        if ((step.resourceType === 'QUIZ' || step.resourceType === 'TEST') && locStepUrl.includes('${')) {
            locStepUrl = locStepUrl.split('?')[0];
        }

        setStepUrl(locStepUrl);
        setParams(locParams);
        setError(undefined);
    }, [step]);

    if (!step) {
        return <CircularLoader />;
    }

    if (error) {
        return <Alert showIcon={true} message={error} type="error" />;
    }

    if (!stepUrl) {
        return <CircularLoader />;
    }

    const dynamicContentCallback = (content) => {
        if (!content) return;

        // Attach a single event listener to the container (event delegation)
        if (!content.trailClickBound) {
            content.addEventListener('click', (event) => {
                const target = event.target;
                if (!target) return;

                if (target.classList.contains('js-pw-continue') || target.classList.contains('js-pw-next-media-item')) {
                    event.preventDefault();

                    if (target.disabled) return;
                    target.disabled = true;

                    if (continueRef.current) {
                        continueRef.current();
                    }
                }
            });
            content.trailClickBound = true;
        }

        updateCssStylesAndClasses(content);
    };

    const activityCallback = (content) => {
        if (!content) return;

        if (step.complete && step.resultLoadFunction) {
            const resultLoadFunction = window[step.resultLoadFunction];
            if (typeof resultLoadFunction === 'function') {
                resultLoadFunction(step, function () {
                    dynamicContentCallback(content);
                }, $(content));
            } else {
                dynamicContentCallback(content);
            }
        } else if (step.loadFunction) {
            const loadFunction = window[step.loadFunction];
            if (typeof loadFunction === 'function') {
                loadFunction(step, function () {
                    dynamicContentCallback(content);
                }, $(content));
            } else {
                dynamicContentCallback(content);
            }
        } else {
            dynamicContentCallback(content);
        }
    };

    return (
        <DynamicContent
            className={className}
            url={stepUrl}
            params={params}
            callback={activityCallback}
            entityId={step.stepId}
        />
    )
}

export default DynamicActivity;